import {
    Box,
    Button,
    Dialog,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import { AddCardRequest } from 'types/card/card-list';
import { useAddCardMutation } from 'store/slices/cards/card/add-card';
import { CardsPersonageTitleData } from 'types/card/card-personage-list';
import SuccessSnackbarEffect from 'component/success-snackbar';
import PageviewIcon from '@mui/icons-material/Pageview';
import ViewProduct from 'component/view-product';
import { useProductTitleSearchListQuery } from 'store/slices/toon-tint/toon-tint';

interface Props {
    openDialog: boolean;
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
    personageList: CardsPersonageTitleData[] | undefined;
    cardCategoryList: string[] | undefined;
}

const exchangePrice = [{ label: 'Gold', value: 'gold' }];

const AddCard: React.FC<Props> = (props: Props) => {
    const {
        openDialog,
        personageList,
        cardCategoryList,
        setOpenDialog,
        refetch,
    } = props;

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<AddCardRequest>();

    const [searchProduct, setSearchProduct] = useState('');
    const [viewProduct, setViewProduct] = useState('');
    const [openViewProductDialog, setOpenViewProductDialog] =
        React.useState(false);

    const { data: dataProductCard, isFetching } =
        useProductTitleSearchListQuery({
            productCategory: 'card',
            productType: 'default',
            searchText: searchProduct,
        });

    const [addCard, { error, isError, isSuccess, isLoading }] =
        useAddCardMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();
    const closeModal = () => {
        setOpenDialog(false);
        reset();
    };
    const onSubmit = (values: AddCardRequest) => {
        addCard(values);
    };

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    useEffect(() => {
        if (isSuccess) {
            refetch();
            closeModal();
        }
    }, [isSuccess]);

    return (
        <>
            <Dialog aria-hidden={false} open={openDialog} onClose={closeModal}>
                <Box
                    display={'flex'}
                    justifyContent={'center'}
                    bgcolor={rgba(colors.primary.main, 0.5)}
                >
                    <DialogTitle
                        fontSize={pxToRem(20)}
                        textTransform={'uppercase'}
                    >
                        ADD NEW CARD
                    </DialogTitle>
                    <DialogIconClose handleClose={closeModal} />
                </Box>

                <DialogContent>
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box display={'flex'} pt={pxToRem(15)}>
                            <Controller
                                name="cardTitle"
                                control={control}
                                rules={{
                                    required: 'Card Title is required',
                                }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Card Title"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />

                            <Controller
                                name="positionInCollection"
                                control={control}
                                rules={{
                                    required:
                                        'Position Collection Title is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10) }}
                                        fullWidth
                                        name={name}
                                        value={value ?? ''}
                                        onChange={(e) =>
                                            onChange(
                                                Number(e.target.value) || '',
                                            )
                                        }
                                        label="Position Collection"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Box display={'flex'} pt={pxToRem(15)}>
                            <FormControl
                                error={Boolean(errors.cardCategory)}
                                fullWidth
                            >
                                <InputLabel>Card Category</InputLabel>
                                <Controller
                                    name="cardCategory"
                                    control={control}
                                    rules={{
                                        required: 'Card Category is required',
                                    }}
                                    render={({
                                        field: { name, value = '', onChange },
                                    }) => (
                                        <Select
                                            label="Card Category"
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                        >
                                            {cardCategoryList?.map((status) => (
                                                <MenuItem
                                                    key={status.toString()}
                                                    value={status.toString()}
                                                >
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.cardCategory && (
                                    <FormHelperText>
                                        {errors.cardCategory.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl
                                sx={{ ml: pxToRem(10) }}
                                error={Boolean(errors.personageId)}
                                fullWidth
                            >
                                <InputLabel>Personage</InputLabel>
                                <Controller
                                    name="personageId"
                                    control={control}
                                    rules={{
                                        required: 'Personage Id is required',
                                    }}
                                    render={({
                                        field: { name, value = '', onChange },
                                    }) => (
                                        <Select
                                            label="Personage"
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                        >
                                            {personageList?.map((status) => (
                                                <MenuItem
                                                    key={status.id.toString()}
                                                    value={status.id.toString()}
                                                >
                                                    {status.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.personageId && (
                                    <FormHelperText>
                                        {errors.personageId.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        <Box display={'flex'} pt={pxToRem(15)}>
                            <FormControl
                                error={Boolean(errors.personageId)}
                                fullWidth
                            >
                                <InputLabel>Exchange Currency</InputLabel>
                                <Controller
                                    name="exchangeCurrency"
                                    control={control}
                                    rules={{
                                        required: 'Exchange Price is required',
                                    }}
                                    render={({
                                        field: { name, value = '', onChange },
                                    }) => (
                                        <Select
                                            label="Exchange Currency"
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                        >
                                            {exchangePrice?.map(
                                                (status, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={status.value.toString()}
                                                    >
                                                        {status.label}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    )}
                                />
                                {errors.personageId && (
                                    <FormHelperText>
                                        {errors.personageId.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <Controller
                                name="exchangePrice"
                                control={control}
                                rules={{
                                    required: 'Exchange Price is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10) }}
                                        fullWidth
                                        name={name}
                                        value={value ?? ''}
                                        onChange={(e) =>
                                            onChange(
                                                Number(e.target.value) || '',
                                            )
                                        }
                                        label="Exchange Price"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Box
                            mt={pxToRem(10)}
                            display={'flex'}
                            alignItems={'center'}
                        >
                            <Controller
                                name="productId"
                                control={control}
                                render={({
                                    field: { onChange, ref, value },
                                }) => (
                                    <Autocomplete
                                        limitTags={5}
                                        disableCloseOnSelect
                                        sx={{
                                            width: viewProduct ? '95%' : '100%',
                                        }}
                                        options={dataProductCard ?? []}
                                        loading={isFetching}
                                        value={
                                            (dataProductCard ?? []).find(
                                                (opt) => opt.id === value,
                                            ) ?? null
                                        }
                                        onChange={(_, newValue) => {
                                            onChange(
                                                newValue ? newValue.id : '',
                                            );
                                            setViewProduct(
                                                newValue ? newValue.id : '',
                                            );
                                        }}
                                        inputValue={searchProduct}
                                        onInputChange={(
                                            _,
                                            newInput,
                                            reason,
                                        ) => {
                                            if (reason !== 'reset') {
                                                setSearchProduct(newInput);
                                            }
                                        }}
                                        getOptionLabel={(option) =>
                                            option.title || ''
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        filterSelectedOptions
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={ref}
                                                label="Product"
                                                variant="outlined"
                                                fullWidth
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFetching && (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )}
                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        ),
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                            {viewProduct && (
                                <Box
                                    pl={pxToRem(10)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Tooltip title="View Product">
                                        <PageviewIcon
                                            fontSize={'large'}
                                            onClick={() => {
                                                setOpenViewProductDialog(true);
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{ width: '50%' }}
                        onClick={() => formRef.current?.requestSubmit()}
                        variant={'contained'}
                        loading={isLoading}
                    >
                        Create Card
                    </Button>
                    <Button
                        sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                        onClick={() => closeModal()}
                        variant={'contained'}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openViewProductDialog}
                onClose={() => setOpenViewProductDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '700px',
                            minHeight: '550px',
                            maxWidth: '90%',
                        },
                    },
                }}
            >
                <ViewProduct
                    setOpenViewProductDialog={setOpenViewProductDialog}
                    viewProduct={viewProduct}
                />
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Card!'}
            />
        </>
    );
};
export default AddCard;
